import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion } from 'framer-motion';

export default function MiniCalendar({ 
  tasks = [], 
  dayNotes = [], 
  onDayClick, 
  selectedDate = new Date().toISOString().split('T')[0] 
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date(selectedDate);
    d.setDate(1);
    return d;
  });

  const nextMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() + 1);
    setCurrentMonth(d);
  };

  const prevMonth = () => {
    const d = new Date(currentMonth);
    d.setMonth(d.getMonth() - 1);
    setCurrentMonth(d);
  };

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const dayNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

  // Calculate days in month
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Day of week of the 1st of the month (0 = Sun, 1 = Mon, ..., 6 = Sat)
  let firstDayIdx = new Date(year, month, 1).getDay();
  // Adjust to make Monday = 0
  firstDayIdx = firstDayIdx === 0 ? 6 : firstDayIdx - 1;

  const days = [];
  // Empty slots for previous month
  for (let i = 0; i < firstDayIdx; i++) {
    days.push(null);
  }
  
  // Real days
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(year, month, i);
    // adjust timezone offset to get local YYYY-MM-DD
    const dateStr = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0')
    ].join('-');
    days.push(dateStr);
  }

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '20px',
      padding: '20px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
      border: '1px solid rgba(226,232,240,0.8)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#1e293b' }}>
          {monthNames[month]} {year}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={prevMonth} style={{ width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' }}>
            <FiChevronLeft size={14} />
          </button>
          <button onClick={nextMonth} style={{ width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569' }}>
            <FiChevronRight size={14} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
        {dayNames.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 800, color: '#94a3b8' }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
        {days.map((dateStr, i) => {
          if (!dateStr) {
            return <div key={`empty-${i}`} style={{ height: '48px' }} />;
          }

          const dayNum = parseInt(dateStr.split('-')[2], 10);
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          
          const dayTasks = tasks.filter(t => t.date === dateStr);
          const noteObj = dayNotes.find(n => n.date === dateStr && n.note);
          
          const completedTasks = dayTasks.filter(t => t.status === 'completed').length;
          const allCompleted = dayTasks.length > 0 && completedTasks === dayTasks.length;
          const someCompleted = completedTasks > 0;

          let bg = 'transparent';
          let border = '1px solid transparent';
          if (isSelected) {
            bg = '#e0e7ff';
            border = '1px solid #c7d2fe';
          } else if (isToday) {
            bg = '#f1f5f9';
            border = '1px solid #cbd5e1';
          }

          return (
            <motion.button
              key={dateStr}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onDayClick && onDayClick(dateStr)}
              style={{
                height: '48px',
                borderRadius: '8px',
                background: bg,
                border: border,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                cursor: 'pointer',
                position: 'relative',
                color: isSelected ? '#4f46e5' : (isToday ? '#0f172a' : '#475569'),
                fontWeight: (isSelected || isToday) ? 800 : 600,
                fontSize: '12px',
                padding: '4px 2px',
                overflow: 'hidden'
              }}
              title={`${dateStr}${noteObj?.note ? ' - ' + noteObj.note : ''}`}
            >
              <span style={{ marginBottom: '2px', lineHeight: 1 }}>{dayNum}</span>
              
              {noteObj?.note && (
                <div style={{ 
                  fontSize: '9px', 
                  color: '#4f46e5', 
                  fontWeight: 800, 
                  whiteSpace: 'nowrap', 
                  textOverflow: 'ellipsis', 
                  overflow: 'hidden', 
                  maxWidth: '90%', 
                  background: 'rgba(79,70,229,0.1)', 
                  padding: '1px 3px', 
                  borderRadius: '3px',
                  marginTop: '2px'
                }}>
                  {noteObj.note.substring(0, 5)}
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '2px', position: 'absolute', bottom: '4px' }}>
                {dayTasks.length > 0 && (
                  <div style={{ 
                    width: '6px', height: '6px', borderRadius: '50%', 
                    background: allCompleted ? '#10b981' : (someCompleted ? '#f59e0b' : '#94a3b8') 
                  }} />
                )}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
